import type { RefinedTrace, Span } from '@repo/shared';

export function transformKey(key: string) {
  if (key.includes('.argument.')) {
    return `Input`;
  }
  if (key.includes('.result')) {
    return 'Output';
  }
  return key.split('.').join(' ').split('_');
}

export function cleanString(string: string) {
  return (
    string
      .replace(/\\n/gu, '')
      // Also handle any actual newlines
      .replace(/\n/gu, '')
      // Clean up any resulting extra spaces
      .replace(/\s+/gu, ' ')
      .trim()
  );
}

export const refineTraces = (
  traces: Array<Span>,
  isWorkflow = false
): Array<RefinedTrace> => {
  const listOfSpanIds = new Set<string>();

  const newName = (name: string) => {
    if (name.startsWith('workflow.') && isWorkflow) {
      return name.split('.').slice(2).join('.');
    }
    if (name.startsWith('agent.') && !isWorkflow) {
      return name.split('.').slice(1).join('.');
    }
    return name;
  };

  const groupedTraces = traces.reduce<Record<string, Array<Span>>>(
    (acc, curr) => {
      const newCurr = {
        ...curr,
        name: newName(curr.name),
        duration: curr.endTime - curr.startTime,
      };

      listOfSpanIds.add(curr.id);

      return {
        ...acc,
        [curr.traceId]: [...(acc[curr.traceId] ?? []), newCurr],
      };
    },
    {}
  );

  const tracesData = Object.entries(groupedTraces).map(([key, value]) => {
    const parentSpan = value.find(
      (span) => !span.parentSpanId || !listOfSpanIds.has(span.parentSpanId)
    );

    const enrichedSpans = value.map((span) => ({
      ...span,
      parentSpanId: parentSpan?.id === span.id ? null : span.parentSpanId,
      relativePercentage: parentSpan ? span.duration / parentSpan.duration : 0,
    }));

    const failedStatus = value.find((span) => span.status.code !== 0)?.status;

    return {
      traceId: key,
      serviceName: parentSpan?.name ?? key,
      duration:
        parentSpan?.duration ??
        value.reduce((acc, curr) => acc + curr.duration, 0),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: failedStatus ?? parentSpan?.status ?? value[0]!.status,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      started: value[0]!.startTime,
      trace: enrichedSpans,
    };
  });

  return tracesData;
};

export const allowedAiSpanAttributes = [
  'operation.name',
  'ai.operationId',
  'ai.model.provider',
  'ai.model.id',
  'ai.prompt.format',
  'ai.prompt.messages',
  'ai.prompt.tools',
  'ai.prompt.toolChoice',
  'ai.settings.toolChoice',
  'ai.schema',
  'ai.settings.output',
  'ai.response.object',
  'ai.response.text',
  'ai.response.timestamp',
  'componentName',
  'ai.usage.promptTokens',
  'ai.usage.completionTokens',
];
